import { appConfig } from "@common-config/app.config";
import { namespace } from "@common-config/constants";
import { Workspace } from "@common-models/workspace";
import { workspaceSelectByQuery } from "@common-reducers/DBServiceThunks";
import { useAppDispatch, useAppSelector } from "@common-reducers/hooks/store.hook";
import { useRouter } from "@common-reducers/hooks/use-router";
import { selectCurrentWorkspace } from "@common-reducers/UserSelectors";
import { switchWorkspace } from "@common-reducers/UserThunks";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import {
    Button,
    Divider,
    Grid,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography
} from "@mui/material";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

type Props = {
    isSupportMode?: boolean,
    drawerIsOpen?: boolean
}

function WorkspaceDropDown({ isSupportMode = false, drawerIsOpen }: Props) {
    const { t } = useTranslation(namespace);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const dispatch = useAppDispatch()
    const userId = useAppSelector((state) => state.UserReducer.userId);

    const router = useRouter();
    const [showTooltip, setShowTooltip] = useState(true);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setShowTooltip(false);
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const workspace = useAppSelector(selectCurrentWorkspace);

    const userWorkspaces: Workspace[] = useAppSelector((state) =>
        workspaceSelectByQuery(state.DBWorkspaceReducer, { deleted: { $ne: true } })
    );
    const handleCloseSwitchWorkspace = useCallback(async (workspaceId?: string) => {

        if (isSupportMode) {
            const userUrl = `${appConfig.APP_ENDPOINT}/account?supportedUserId=${userId}&workspaceId=${workspaceId}`;

            window.location.href = userUrl;
            setAnchorEl(null);
            return;
            // window.open(userUrl, '_blank');
        }

        setAnchorEl(null);
        if (userId && workspaceId) {
            const result = await dispatch(switchWorkspace({ workspaceId })).unwrap();

            if (result.success) {
                // Only navigate if not already on tasks page
                const currentPath = router.location.pathname;
                if (currentPath !== '/tasks') {
                    router.navigate('tasks');
                }
            }
        }
    }, [userId, dispatch, router, isSupportMode]);

    return (
        <>
            <Tooltip
                disableHoverListener={!showTooltip}
                componentsProps={{
                    tooltip: {
                        sx: {
                            display: drawerIsOpen ? "none" : "flex",
                            bgcolor: "var(--color-surface)",
                            padding: "4px 18px",
                            maxWidth: "150px",
                            height: "35px",
                            justifyContent: "center",
                            border: '1px solid transparent',
                            alignItems: "center",
                            color: "rgb(0, 55, 80)",
                            fontSize: "14px",
                            fontFamily: "'Inter', sans-serif",
                            boxShadow: "0 5px 12px rgb(0 0 0 / 15%)",
                            "& .MuiTooltip-arrow": {
                                color: "var(--color-surface)",
                            },
                        },
                    },
                }}
                arrow
                title={t('workspace.title')}
                placement="right-start">
                <Stack
                    direction="row"
                    alignItems={"center"}
                    justifyContent={drawerIsOpen ? "space-between" : "center"}
                    sx={{
                        margin: "16px 0 0 0", paddingInline: "16px"
                    }}>


                    <Button onClick={handleClick}
                        sx={{
                            width: "100%",
                            display: "flex", justifyContent: drawerIsOpen ? "space-between" : "center", alignItems: "center",
                            color: "var(--color-primary)", textTransform: "capitalize",
                        }}>
                        <Stack direction="row" alignItems={"center"} >
                            <WorkspacesIcon sx={{ marginInlineEnd: drawerIsOpen ? "5px" : "" }} />
                            {drawerIsOpen ? !isSupportMode ? t('workspace.title') : "Choose workspace to support" : ""}
                        </Stack>
                        {drawerIsOpen ? !anchorEl ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon /> : null}
                    </Button>

                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        <Typography style={{ margin: '10px', fontSize: "12px", color: "var(--color-primary)", textTransform: "capitalize" }}>{t('workspace.myWorkspaces')}</Typography>


                        {userWorkspaces?.map((ws, index) => <MenuItem key={`switch-ws-${index}`} disabled={workspace?._id === ws._id} onClick={() => handleCloseSwitchWorkspace(ws._id as string)}>{ws.name}</MenuItem>)}

                        <Divider />
                        {!isSupportMode && <Grid gap={2} container justifyContent="space-between" style={{ padding: '10px' }}>
                            <Link to="/new-workspace">
                                <Button
                                    sx={{ color: "var(--color-primary)", textTransform: "capitalize" }}
                                    startIcon={<AddIcon />}
                                    size="small"
                                    onClick={handleClose}
                                >
                                    {t('workspace.addWorkspace')}
                                </Button>
                            </Link>
                            <Link to="/account-workspace">
                                <Button
                                    sx={{ color: "var(--color-primary)", textTransform: "capitalize" }}

                                    startIcon={<ViewCompactIcon />}
                                    size="small"
                                    onClick={handleClose}
                                >
                                    {t('workspace.browseAll')}
                                </Button>
                            </Link>
                        </Grid>}


                    </Menu>
                </Stack>
            </Tooltip>
        </>
    );
}

export default WorkspaceDropDown;
